import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import React, { CSSProperties, Fragment, ReactNode } from 'react';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import cn from 'classnames';

interface ModalProps {
  show: boolean;
  onClose: () => void;
  children: ReactNode;
  title: string | ReactNode;
  childrenPadding?: string;
  scrollable?: boolean;
  maxWidthOverride?: string;
  containerClassname?: string;
  headerClassname?: string;
  headerBackgroundOverride?: CSSProperties['background'];
  headerPaddingClassname?: string;
  closeButtonClassname?: string;
  closeIconOverrideClassname?: string;
}

export const Modal = ({
  show,
  onClose,
  children,
  title,
  childrenPadding,
  scrollable,
  maxWidthOverride,
  containerClassname,
  headerClassname,
  headerBackgroundOverride,
  headerPaddingClassname,
  closeButtonClassname,
  closeIconOverrideClassname,
}: ModalProps) => {
  const childPadding = childrenPadding ?? 'p-6';
  const headerPadding = headerPaddingClassname ?? 'p-6';
  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog
        className="z-overlay font-sans relative"
        onClose={() => onClose()}
      >
        <div className="fixed inset-0 bg-black/10" aria-hidden="true" />
        <div
          className={cn(
            'fixed inset-0 flex w-full items-center justify-center p-4 m-auto',
            maxWidthOverride ?? 'max-w-screen-xl',
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-opacity-50 bg-gray-1000" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className={cn(
                'relative max-h-screen inline-flex flex-col w-full max-w-8xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-12px',
                containerClassname,
              )}
            >
              <Dialog.Title
                as="h3"
                style={{
                  background: headerBackgroundOverride
                    ? headerBackgroundOverride
                    : 'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #0A56A2 0%, #012C57 72.37%, #012C57 100%)',
                }}
                className={cn(
                  'relative flex items-center justify-between font-sans text-xl font-normal leading-6 text-white',
                  headerPadding,
                  headerClassname,
                )}
              >
                {title}
                <button
                  aria-label="Close modal"
                  data-testid="close-modal"
                  onClick={() => onClose()}
                  type="button"
                  className={cn(
                    'inline-block p-1.5 hover:bg-blue-900 rounded-md bg-opacity-20 transition-colors',
                    closeButtonClassname,
                  )}
                >
                  <IconCloseSvg
                    svg={{
                      className: closeIconOverrideClassname
                        ? closeIconOverrideClassname
                        : 'w-4 text-white',
                    }}
                  />
                </button>
              </Dialog.Title>
              <div
                className={cn(
                  childPadding,
                  scrollable ? 'overflow-y-scroll' : '',
                )}
              >
                {children}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
